import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import { PaginationProps } from 'ui/molecules/pagination';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'src/ui/atoms/translate';
import { AdminApi, AdminGroup, AdminGroupsListOrderingEnum, AdminGroupsValuesRetrieveFieldEnum } from 'src/api';
import WithDataRecord from 'src/hoc/WithDataRecord';
import useApiCall from 'src/hooks/use-api-call';
import { useDispatch } from 'store/hooks';
import { push } from 'connected-react-router';
import makeLink from 'src/helper/make-link';
import { ISSUER_ROUTES } from '../../routes.config';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { Search } from 'src/ui/molecules/filtering';

export interface GroupsTableProps {
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  goToDetails: (userId: string) => void;
  groups?: AdminGroup[];
  paginationProps?: PaginationProps;
  userId?: string;
  loading?: boolean;
}

const GroupsTable: FunctionComponent<GroupsTableProps> = (props) => {
  const { className, loading, ordering, onOrderBy, groups, userId, paginationProps, goToDetails } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  return (
    <div className={cx('groups-table', className)}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={groups}
        paginationProps={paginationProps}
        loading={loading}
        onGoToDetails={goToDetails}
        searchComponent={
          <Search input="search" defaultField="search" label={translate('filters.labels.groupId')} name="search" />
        }
        cells={[
          {
            title: <Translate name="issuerPermissionsGroupsTable.group" />,
            render: ({ name }) => name,
            orderingFieldName: AdminGroupsListOrderingEnum.NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'name',
            searchFieldName: AdminGroupsValuesRetrieveFieldEnum.NAME,
            getFilterData: (params: any) => adminApi.adminGroupsValuesRetrieve({ ...params, userId: [props.userId] }),
          },
          {
            title: <Translate name="issuerPermissionsGroupsTable.organization" />,
            render: ({ organizationName }) => organizationName,
            orderingFieldName: AdminGroupsListOrderingEnum.ORGANIZATION_NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'organizationName',
            searchFieldName: AdminGroupsValuesRetrieveFieldEnum.ORGANIZATION_NAME,
            getFilterData: (params: any) => adminApi.adminGroupsValuesRetrieve({ ...params, userId: [props.userId] }),
          },
        ]}
      />
    </div>
  );
};

interface UserGroupsTableProps {
  title?: ReactNode;
  filters?: ReactNode;
  skipFilterUrlSync?: boolean;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  userId: string;
}

const UserGroupsTable: FunctionComponent<UserGroupsTableProps> = WithDataRecord<AdminApi, AdminGroup>(AdminApi)(
  ({ data, ordering, onOrderBy, loading, filters, paginationProps, userId }) => {
    const dispatch = useDispatch();

    const goTo = (groupId: string) => {
      dispatch(push(makeLink(ISSUER_ROUTES.groupDetails, { groupId })));
    };

    return (
      <>
        {filters && <>{filters}</>}
        <GroupsTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          groups={data}
          loading={loading}
          paginationProps={paginationProps}
          goToDetails={(groupId: string) => goTo(groupId)}
          userId={userId}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminGroupsList({
      offset,
      limit,
      ordering: ordering as AdminGroupsListOrderingEnum,
      ...props,
      userId: [props.userId],
    });
  },
);

export default UserGroupsTable;
